/** 低代码规则编辑器（F-008）：条件—动作—优先级，保存期实时冲突检测（AC-F008-02）。 */
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { findConflicts } from '../../strategy/rule-engine/conflict-check';
import type { Rule } from '../../strategy/rule-engine/rule-model';
import type { PredicateField, PredicateOp } from '../../strategy/rule-engine/rule-model';
import { cn } from '../cn';

interface Props {
  rules: Rule[];
  onSetRule: (i: number, p: Partial<Rule>) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}

const OPS_BY_FIELD: Record<PredicateField, PredicateOp[]> = {
  'call.waitMs': ['>', '>=', '<', '<=', '=='],
  'call.direction': ['=='],
  'call.floor': ['>', '<', '=='],
};

const selectCls = 'bg-input/40 border border-border rounded px-1.5 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60';
const inputCls = 'bg-input/40 border border-border rounded px-2 py-1 text-xs font-mono text-foreground w-20 focus:outline-none focus:ring-1 focus:ring-primary/60';

export function RuleEditor({ rules, onSetRule, onAdd, onRemove }: Props) {
  const conflicts = findConflicts(rules);
  const conflictPairs = new Set(conflicts.flatMap((c) => [c.ruleA, c.ruleB]));

  return (
    <div className="flex flex-col gap-3">
      {rules.length === 0 && <p className="text-xs text-muted-foreground">暂无规则；未命中呼叫将走兜底（最近轿厢）。</p>}
      <div className="grid grid-cols-[auto_1fr_auto_1fr_1fr_auto] gap-2 px-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        <span>优先级</span><span>条件字段</span><span>运算</span><span>值</span><span>动作</span><span />
      </div>
      {rules.map((rule, i) => {
        const cond = rule.conditions[0]!;
        const field = cond.field;
        const conflict = conflictPairs.has(rule.name);
        const setCond = (p: Partial<{ field: PredicateField; op: PredicateOp; value: number | string }>) =>
          onSetRule(i, { conditions: [{ ...cond, ...p }] });
        return (
          <div key={i} className={cn('grid grid-cols-[auto_1fr_auto_1fr_1fr_auto] gap-2 items-center p-2 rounded border', conflict ? 'border-accent/40 bg-accent/5' : 'border-border bg-secondary/30')}>
            <div className="flex items-center gap-1">
              {conflict && <AlertTriangle className="w-3 h-3 text-accent" />}
              <input type="number" value={rule.priority} onChange={(e) => onSetRule(i, { priority: Number(e.target.value) })} className={cn(inputCls, 'w-10 text-center')} />
            </div>
            <select value={field} onChange={(e) => onSetRule(i, { conditions: [{ field: e.target.value as PredicateField, op: OPS_BY_FIELD[e.target.value as PredicateField][0]!, value: cond.value }] })} className={selectCls}>
              <option value="call.waitMs">等待时长</option>
              <option value="call.direction">呼叫方向</option>
              <option value="call.floor">呼叫楼层</option>
            </select>
            <select value={cond.op} onChange={(e) => setCond({ op: e.target.value as PredicateOp })} className={selectCls}>
              {OPS_BY_FIELD[field].map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
            {field === 'call.direction' ? (
              <select value={typeof cond.value === 'string' ? cond.value : 'up'} onChange={(e) => setCond({ value: e.target.value })} className={selectCls}>
                <option value="up">上行</option>
                <option value="down">下行</option>
              </select>
            ) : (
              <input type="number" value={typeof cond.value === 'number' ? cond.value : 0} onChange={(e) => setCond({ value: Number(e.target.value) })} className={inputCls} />
            )}
            <select value={rule.action.type} onChange={(e) => onSetRule(i, { action: { type: e.target.value as Rule['action']['type'] } })} className={selectCls}>
              <option value="assign-nearest">指派最近空闲</option>
              <option value="assign-min-eta">指派最快到达</option>
            </select>
            <button onClick={() => onRemove(i)} className="w-7 h-7 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
      <button onClick={onAdd} className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"><Plus className="w-4 h-4" />添加规则</button>
      {conflicts.length > 0 && (
        <div className="text-xs text-accent bg-accent/5 border border-accent/30 rounded-md p-2 space-y-1">
          {conflicts.map((c, idx) => <div key={idx}>「{c.ruleA}」与「{c.ruleB}」：{c.reason}</div>)}
        </div>
      )}
    </div>
  );
}
